import React, { useState, useEffect, useCallback } from 'react';
import Menu from '../../components/Menu/Menu';
import Footer from '../../components/Footer/Footer';
import Toast from '../../components/Toast/Toast';
import { getCapitalGains, downloadTaxCsv } from '../../services/TaxService';
import { processError } from '../../services/ServiceUtils';
import { getErrorMessage } from '../../utils/MessageUtils';

const fmtBRL = v => (v === null || v === undefined || isNaN(Number(v)))
    ? '—'
    : Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

// Quantidade de cripto: até 8 casas, sem zeros à toa (0,5 XRP e 0,00012345 BTC na mesma coluna).
const fmtQty = v => (v === null || v === undefined || isNaN(Number(v)))
    ? '—'
    : Number(v).toLocaleString('pt-BR', { maximumFractionDigits: 8 });

function yearOptions() {
    const now = new Date().getFullYear();
    const years = [];
    for (let y = now; y >= 2020; y--) years.push(y);
    return years;
}

function TaxReport() {
    const [year, setYear] = useState(new Date().getFullYear() - 1);
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(false);
    const [notification, setNotification] = useState({});

    const load = useCallback(() => {
        setLoading(true);
        getCapitalGains(year, 'production')
            .then(r => setReport(r))
            .catch(err => setNotification(getErrorMessage(processError(err))))
            .finally(() => setLoading(false));
    }, [year]);

    useEffect(() => { load(); }, [load]);

    function onDownload(type) {
        downloadTaxCsv(year, type, 'production')
            .catch(err => setNotification(getErrorMessage(processError(err))));
    }

    const totals = report ? report.totals : {};
    const months = report ? report.months : [];
    const positions = report ? report.positions : [];

    return (
        <React.Fragment>
            <Menu />
            <main className='content'>
                <div className='row py-4 align-items-center'>
                    <div className='col-md-6'>
                        <h2 className='h4'>Imposto de Renda <span className='badge bg-secondary ms-2' style={{ fontSize: '0.5em', verticalAlign: 'middle' }}>Ganho de Capital</span></h2>
                        <p className='text-muted mb-0' style={{ fontSize: '0.85em' }}>Apuração sobre operações reais (produção). Cripto à vista (spot).</p>
                    </div>
                    <div className='col-md-3'>
                        <label className='form-label mb-1' style={{ fontSize: '0.8em' }}>Ano-calendário</label>
                        <select className='form-select' value={year} onChange={e => setYear(parseInt(e.target.value))}>
                            {yearOptions().map(y => <option key={y} value={y}>{y}</option>)}
                        </select>
                    </div>
                    <div className='col-md-3 text-end'>
                        {loading && <span className='spinner-border spinner-border-sm text-primary' role='status' />}
                    </div>
                </div>

                {/* Cards de resumo */}
                <div className='row'>
                    <div className='col-md-3 mb-3'>
                        <div className='card border-0 shadow'>
                            <div className='card-body'>
                                <h6 className='text-muted mb-1'>Imposto devido no ano</h6>
                                <h4 className={`mb-0 ${totals.impostoBRL > 0 ? 'text-danger' : 'text-success'}`}>{fmtBRL(totals.impostoBRL)}</h4>
                                <small className='text-muted'>{totals.mesesComImposto || 0} mês(es) com DARF</small>
                            </div>
                        </div>
                    </div>
                    <div className='col-md-3 mb-3'>
                        <div className='card border-0 shadow'>
                            <div className='card-body'>
                                <h6 className='text-muted mb-1'>Ganho de capital</h6>
                                <h4 className={`mb-0 ${totals.ganhoBRL >= 0 ? 'text-success' : 'text-danger'}`}>{fmtBRL(totals.ganhoBRL)}</h4>
                                <small className='text-muted'>{totals.trades || 0} trades fechados</small>
                            </div>
                        </div>
                    </div>
                    <div className='col-md-3 mb-3'>
                        <div className='card border-0 shadow'>
                            <div className='card-body'>
                                <h6 className='text-muted mb-1'>Alienações no ano</h6>
                                <h4 className='mb-0'>{fmtBRL(totals.alienacoesBRL)}</h4>
                                <small className='text-muted'>vendas (isenção R$35k/mês)</small>
                            </div>
                        </div>
                    </div>
                    <div className='col-md-3 mb-3'>
                        <div className='card border-0 shadow'>
                            <div className='card-body'>
                                <h6 className='text-muted mb-1'>Declarar IN 1.888</h6>
                                <h4 className={`mb-0 ${totals.mesesComDeclaracaoIN1888 > 0 ? 'text-warning' : 'text-success'}`}>{totals.mesesComDeclaracaoIN1888 || 0} mês(es)</h4>
                                <small className='text-muted'>movimentação &gt; R$30k</small>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Situação / obrigações — resposta direta "eu preciso declarar?" */}
                {report && report.obrigacoes && (
                    <div className='alert alert-primary' role='alert'>
                        <h6 className='fw-bold mb-2'>📋 Sua situação em {year}</h6>
                        <ul className='mb-1' style={{ paddingLeft: '1.1rem' }}>
                            <li>{report.obrigacoes.in1888.obrigatorio
                                ? <><b>⚠ Declarar a IN 1.888</b> em {report.obrigacoes.in1888.meses.length} mês(es) ({report.obrigacoes.in1888.meses.join(', ')}) — movimentação acima de R$30k em corretora estrangeira/P2P.</>
                                : <>✓ Nenhum mês exigiu a IN 1.888 (movimentação sempre ≤ R$30k).</>}</li>
                            <li>{report.obrigacoes.darf.devido
                                ? <><b>⚠ Imposto a pagar (DARF 4600)</b>: {fmtBRL(report.obrigacoes.darf.totalBRL)} em {report.obrigacoes.darf.meses.length} mês(es).</>
                                : <>✓ Sem imposto de ganho de capital no ano (meses isentos ou sem ganho).</>}</li>
                            <li>{report.obrigacoes.bensDireitos.criptosADeclarar > 0
                                ? <><b>⚠ {report.obrigacoes.bensDireitos.criptosADeclarar} cripto(s)</b> com custo ≥ R$5k para lançar em Bens e Direitos (grupo 08).</>
                                : <>✓ Nenhuma cripto em aberto acima de R$5k para Bens e Direitos.</>}</li>
                        </ul>
                        <small className='text-muted'>{report.obrigacoes.nota}</small>
                    </div>
                )}

                {/* Botões de export */}
                <div className='row mb-3'>
                    <div className='col-12'>
                        <div className='btn-toolbar' role='toolbar'>
                            <button className='btn btn-sm btn-outline-primary me-2 mb-1' onClick={() => onDownload('monthly')}>⬇ Resumo mensal (GCAP)</button>
                            <button className='btn btn-sm btn-outline-primary me-2 mb-1' onClick={() => onDownload('operations')}>⬇ Operações (trades)</button>
                            <button className='btn btn-sm btn-outline-primary me-2 mb-1' onClick={() => onDownload('positions')}>⬇ Posição anual (Bens e Direitos)</button>
                            <button className='btn btn-sm btn-outline-warning me-2 mb-1' onClick={() => onDownload('in1888')}>⬇ IN 1.888 (transações)</button>
                        </div>
                        <small className='text-muted d-block mt-1'>CSV (Excel pt-BR). O arquivo IN 1.888 tem colunas <b>provisórias</b> — confira contra o leiaute oficial v1.2 antes de submeter.</small>
                    </div>
                </div>

                {/* Resumo mensal */}
                <div className='card border-0 shadow mb-4'>
                    <div className='card-header'><h5 className='mb-0'>Resumo mensal</h5></div>
                    <div className='card-body table-responsive'>
                        <table className='table table-hover align-middle'>
                            <thead>
                                <tr>
                                    <th>Mês</th><th className='text-end'>Trades</th><th className='text-end'>Alienações</th>
                                    <th className='text-end'>Ganho</th><th className='text-center'>Isento?</th>
                                    <th className='text-end'>Imposto (4600)</th><th className='text-center'>Venc.</th>
                                    <th className='text-end'>Movimentação</th><th className='text-center'>IN 1.888?</th>
                                </tr>
                            </thead>
                            <tbody>
                                {months.length === 0 && <tr><td colSpan={9} className='text-center text-muted'>Sem operações no período.</td></tr>}
                                {months.map(m => (
                                    <tr key={m.month}>
                                        <td>{m.month}</td>
                                        <td className='text-end'>{m.trades}</td>
                                        <td className='text-end'>{fmtBRL(m.alienacoesBRL)}</td>
                                        <td className={`text-end ${m.ganhoBRL >= 0 ? 'text-success' : 'text-danger'}`}>{fmtBRL(m.ganhoBRL)}</td>
                                        <td className='text-center'>{m.isento ? <span className='badge bg-success'>isento</span> : <span className='badge bg-danger'>tributa</span>}</td>
                                        <td className='text-end'>{m.impostoBRL > 0 ? <b>{fmtBRL(m.impostoBRL)}</b> : '—'}</td>
                                        <td className='text-center'><small>{m.darfVencimento || '—'}</small></td>
                                        <td className='text-end'>{fmtBRL(m.operacoesBRL)}</td>
                                        <td className='text-center'>{m.declaracaoIN1888 ? <span className='badge bg-warning text-dark'>declarar</span> : <span className='badge bg-light text-dark'>não</span>}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Posição anual (Bens e Direitos) */}
                <div className='card border-0 shadow mb-4'>
                    <div className='card-header'><h5 className='mb-0'>Posição em 31/12 — Bens e Direitos (grupo 08)</h5></div>
                    <div className='card-body table-responsive'>
                        <table className='table table-hover align-middle'>
                            <thead>
                                <tr><th>Cripto</th><th>Símbolo</th><th className='text-end'>Quantidade</th><th className='text-end'>Custo de aquisição (BRL)</th><th className='text-center'>Declarar? (≥R$5k)</th></tr>
                            </thead>
                            <tbody>
                                {positions.length === 0 && <tr><td colSpan={5} className='text-center text-muted'>Sem posição em aberto.</td></tr>}
                                {positions.map(p => (
                                    <tr key={p.base}>
                                        <td>{p.base}{p.semCambio && <span className='badge bg-warning text-dark ms-2'>câmbio parcial</span>}</td>
                                        <td>{p.symbol}</td>
                                        <td className='text-end'>{p.qty}</td>
                                        <td className='text-end'>{fmtBRL(p.custoBRL)}</td>
                                        <td className='text-center'>{p.declararBemDireito ? <span className='badge bg-warning text-dark'>declarar</span> : <span className='badge bg-light text-dark'>não</span>}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Notas */}
                {report && report.futuresInfo && report.futuresInfo.ordens > 0 && (
                    <div className='alert alert-info' role='alert'>
                        <b>Futuros:</b> {report.futuresInfo.ordens} ordens de derivativos não entram nesta apuração ({report.futuresInfo.nota})
                    </div>
                )}
                {report && report.alienacoesSemCusto && report.alienacoesSemCusto.length > 0 && (
                    // Vendas cujo custo de aquisição o robô não conhece (cripto que entrou de fora:
                    // transferência, airdrop, staking). Ficam fora do cálculo — não se inventa custo
                    // —, mas precisam aparecer: apuração a menos é problema fiscal de quem declara.
                    <div className='alert alert-warning' role='alert'>
                        <b>⚠ {report.alienacoesSemCusto.length} venda(s) sem custo de aquisição no histórico</b> —
                        ficaram FORA do cálculo do imposto porque o robô não sabe por quanto essa cripto foi
                        adquirida (recebida de fora, transferida de outra corretora ou anterior ao histórico).
                        Leve ao seu contador:
                        <ul className='mb-0 mt-2'>
                            {report.alienacoesSemCusto.slice(0, 10).map((a, i) => (
                                <li key={i}>
                                    {a.sellDate} — {fmtQty(a.qty)} {a.base} vendidos por {fmtBRL(a.proceedsBRL)}
                                </li>
                            ))}
                            {report.alienacoesSemCusto.length > 10 &&
                                <li>… e mais {report.alienacoesSemCusto.length - 10}.</li>}
                        </ul>
                    </div>
                )}
                {report && (
                    <div className='alert alert-secondary' role='alert' style={{ fontSize: '0.85em' }}>
                        {report.disclaimer}
                        {report.fxCoverage && report.fxCoverage.fonte && report.fxCoverage.fonte.length > 0 &&
                            <span> Fonte do câmbio: {report.fxCoverage.fonte.join(', ')}.</span>}
                        {totals.operacoesSemCambio > 0 &&
                            <span className='text-danger'> ⚠ {totals.operacoesSemCambio} operação(ões) sem câmbio resolvido — fora do cálculo.</span>}
                    </div>
                )}

                <Footer />
            </main>
            <Toast type={notification.type} text={notification.text} />
        </React.Fragment>
    );
}

export default TaxReport;
